import { useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, FileText, Info, MessageSquareText, Paperclip, RotateCcw, SendHorizontal, StickyNote, X } from "lucide-react";
import { Button, EmptyState, Input, NoticeBox, SegmentedControl, Tags } from "../ui";
import { addFiles, formatFileSize, formatMessageTime } from "../copilot/format";
import { COPILOT_TEXT, categoryLabel } from "../copilot/copilotText";
import { useStickyScroll } from "../copilot/useStickyScroll";
import { eventLabel } from "./copilotAdminText";
import { StatusTag } from "./parts";
import SavedRepliesDialog from "./SavedRepliesDialog";

function Attachment({ file }) {
  const isImage = file.mimeType?.startsWith("image/");
  return (
    <a className="ca-attachment" href={file.url} target="_blank" rel="noopener noreferrer">
      {isImage ? <img src={file.url} alt={file.name} loading="lazy" /> : <FileText size={18} aria-hidden="true" />}
      <span className="ca-attachment-name">{file.name}</span>
      <span className="ca-file-size">{formatFileSize(file.size)}</span>
    </a>
  );
}

function Bubble({ message, lang, t, customerName }) {
  if (message.kind === "event") {
    return (
      <li className={`ca-event ${message.visibility === "internal" ? "internal" : ""}`}>
        <span>{eventLabel(message.text, lang)}</span>
        <time dateTime={message.createdAt}>{formatMessageTime(message.createdAt, lang)}</time>
      </li>
    );
  }
  const fromCustomer = message.senderType === "customer";
  const note = message.kind === "note";
  const who = fromCustomer ? message.senderName || customerName || t.customerLabel : message.senderType === "ai" ? "AI" : message.senderName || t.system;
  return (
    <li className={`ca-msg ${fromCustomer ? "customer" : "agent"} ${note ? "note" : ""}`}>
      <span className="ca-msg-who">
        {who}
        {note && <Tags size="sm" variant="warning" className="ca-note-tag" icon={<StickyNote size={11} aria-hidden="true" />}>{t.internalBadge}</Tags>}
      </span>
      <div className="ca-bubble">
        {message.text && <p>{message.text}</p>}
        {message.attachments?.map((file) => <Attachment key={file.id} file={file} />)}
      </div>
      <span className="ca-msg-time">
        <time dateTime={message.createdAt}>{formatMessageTime(message.createdAt, lang)}</time>
        {fromCustomer && !message.readByAdminAt && <span className="ca-unread-flag" aria-label={t.unreadBadge(1)} />}
        {note ? <span>{t.staffOnly}</span> : null}
      </span>
    </li>
  );
}

function Pending({ entry, inbox, id }) {
  const { t } = inbox;
  const failed = entry.status === "failed";
  return (
    <li className={`ca-msg agent pending ${entry.internal ? "note" : ""} ${failed ? "failed" : ""}`}>
      <span className="ca-msg-who">{t.you}</span>
      <div className="ca-bubble">
        {entry.text && <p>{entry.text}</p>}
        {entry.files.map((file, index) => (
          <span className="ca-attachment" key={`${file.name}-${index}`}>
            <FileText size={18} aria-hidden="true" />
            <span className="ca-attachment-name">{file.name}</span>
          </span>
        ))}
      </div>
      <span className="ca-msg-time" role={failed ? "alert" : undefined}>
        {failed ? (
          <>
            <span className="ca-failed">{entry.error || t.failed}</span>
            <Button variant="primary" appearance="text" size="xs" icon={<RotateCcw size={12} aria-hidden="true" />} onClick={() => inbox.retry(id, entry.key)}>{t.retrySend}</Button>
            <Button variant="neutral" appearance="text" size="xs" icon={<X size={12} aria-hidden="true" />} onClick={() => inbox.discardPending(id, entry.key)}>{t.discard}</Button>
          </>
        ) : (
          t.sending
        )}
      </span>
    </li>
  );
}

const EMPTY = { text: "", files: [], internal: false };

export default function ConversationPane({ inbox, lang, selectedId, access, agentName, onBack, onDetails, onAskResolve, onAskAssign }) {
  const { t, detail, categories } = inbox;
  const conversation = detail.conversation;
  const messages = detail.messages;
  const pending = detail.pending || [];
  const draft = inbox.drafts[selectedId] || EMPTY;
  const [fileError, setFileError] = useState(null);
  const [showReplies, setShowReplies] = useState(false);
  const fileInput = useRef(null);
  const box = useRef(null);
  const rtl = lang === "ar";
  const BackIcon = rtl ? ArrowRight : ArrowLeft;
  const lastSeq = messages.length ? messages[messages.length - 1].seq : 0;
  const { scroller, onScroll, showNew, jumpToBottom, rememberPosition } = useStickyScroll({
    id: selectedId,
    lastSeq,
    count: messages.length,
    pendingCount: pending.length,
    ownLast: messages[messages.length - 1]?.senderType === "agent",
    prepended: detail.prepended,
  });

  const resolved = conversation?.status === "resolved";
  const internal = draft.internal;
  // Customer-visible replies are blocked on resolved conversations (reopen
  // first); internal notes never are.
  const replyBlocked = resolved && !internal;
  const tooLong = draft.text.length > 5000;
  const canSend = (draft.text.trim() || draft.files.length) && !tooLong && !replyBlocked;

  const submit = (event) => {
    event?.preventDefault();
    if (!canSend) return;
    inbox.send(selectedId);
    box.current?.focus();
  };
  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };
  const onFiles = (event) => {
    const result = addFiles(draft.files, Array.from(event.target.files || []), COPILOT_TEXT[lang] || COPILOT_TEXT.en);
    inbox.setDraft(selectedId, { files: result.files });
    setFileError(result.error);
    event.target.value = "";
  };

  const status = conversation?.status;
  const nextActions = !access.changeStatus || !conversation
    ? []
    : status === "open"
      ? [{ to: "pending", label: t.markPending }]
      : status === "pending"
        ? [{ to: "open", label: t.markOpen }]
        : [];

  if (detail.status === "notfound") {
    return <EmptyState variant="error" className="ca-fill" action={<Button variant="neutral" appearance="outline" size="sm" onClick={onBack}>{t.back}</Button>}>{t.notFound}</EmptyState>;
  }
  if (detail.status === "error" && !messages.length) {
    return <EmptyState variant="error" className="ca-fill" action={<Button variant="neutral" appearance="outline" size="sm" onClick={() => inbox.loadDetail(selectedId)}>{t.retry}</Button>}>{detail.error || t.detailError}</EmptyState>;
  }

  return (
    <div className="ca-chat">
      <header className="ca-chat-head">
        <Button variant="neutral" appearance="ghost" size="sm" className="ca-back" onClick={onBack} aria-label={t.back} title={t.back} icon={<BackIcon size={18} aria-hidden="true" />} />
        <div className="ca-chat-title">
          <strong>{conversation?.subject || t.loading}</strong>
          {conversation && (
            <span className="ca-chat-sub">
              <span dir="ltr">{conversation.reference}</span>
              <span>{categoryLabel(categories, conversation.category, lang)}</span>
              <span>{conversation.customer.name}</span>
            </span>
          )}
        </div>
        {conversation && <StatusTag status={conversation.status} t={t} />}
        <Button variant="neutral" appearance="ghost" size="sm" className="ca-details-toggle" onClick={onDetails} aria-label={t.details} title={t.details} icon={<Info size={18} aria-hidden="true" />} />
      </header>

      {conversation && (
        <div className="ca-chat-actions">
          <span className="ca-assignee" title={t.assignedTo}>
            {t.assignedTo}: <strong>{conversation.assignedTo?.name || (conversation.assignedTo ? t.assignedAgentBadge : t.unassignedLabel)}</strong>
          </span>
          <span className="ca-spacer" />
          {access.assign && <Button size="sm" variant="neutral" appearance="outline" disabled={Boolean(inbox.busy)} onClick={onAskAssign}>{conversation.assignedTo ? t.reassign : t.assign}</Button>}
          {nextActions.map((action) => (
            <Button key={action.to} size="sm" variant="neutral" appearance="outline" loading={inbox.busy === "status"} disabled={Boolean(inbox.busy)} onClick={() => inbox.changeStatus(action.to)}>{action.label}</Button>
          ))}
          {access.changeStatus && status !== "resolved" && <Button size="sm" variant="success" appearance="filled" disabled={Boolean(inbox.busy)} onClick={onAskResolve}>{t.resolve}</Button>}
          {access.changeStatus && status === "resolved" && <Button size="sm" variant="primary" appearance="filled" loading={inbox.busy === "status"} disabled={Boolean(inbox.busy)} onClick={() => inbox.changeStatus("open")}>{t.reopen}</Button>}
        </div>
      )}

      {inbox.notice && (
        <div className="ca-notice-wrap" role={inbox.notice.type === "error" ? "alert" : "status"}>
          <NoticeBox variant={inbox.notice.type === "error" ? "danger" : "success"} size="sm">{inbox.notice.text}</NoticeBox>
          <Button variant="neutral" appearance="ghost" size="xs" onClick={inbox.dismissNotice} aria-label={t.close} title={t.close} icon={<X size={14} aria-hidden="true" />} />
        </div>
      )}

      <div className="ca-scroll" ref={scroller} onScroll={onScroll}>
        {detail.status === "loading" && !messages.length && <EmptyState variant="loading" compact>{t.loading}</EmptyState>}
        {detail.hasMore && (
          <div className="ca-more">
            <Button variant="neutral" appearance="ghost" size="sm" loading={detail.loadingEarlier} onClick={() => { rememberPosition(); inbox.loadEarlier(); }}>{t.loadEarlier}</Button>
          </div>
        )}
        <ol className="ca-messages" role="log" aria-live="polite" aria-relevant="additions" aria-label={conversation?.subject || t.viewChat}>
          {messages.map((message) => <Bubble key={message.id} message={message} lang={lang} t={t} customerName={conversation?.customer.name} />)}
          {pending.map((entry) => <Pending key={entry.key} entry={entry} inbox={inbox} id={selectedId} />)}
        </ol>
        {detail.error && messages.length > 0 && <p className="ca-error" role="alert">{detail.error}</p>}
      </div>

      {showNew && <Button variant="primary" appearance="filled" size="sm" rounded className="ca-new-pill" icon={<ArrowDown size={14} aria-hidden="true" />} onClick={jumpToBottom}>{t.newMessages}</Button>}

      {access.reply && conversation && (
        <form className={`ca-composer ${internal ? "is-note" : ""}`} onSubmit={submit}>
          <div className="ca-modes">
            <SegmentedControl
              size="sm"
              label={t.composerModes}
              value={internal ? "note" : "reply"}
              options={[{ value: "reply", label: t.reply }, { value: "note", label: t.internalNote }]}
              onChange={(value) => inbox.setDraft(selectedId, { internal: value === "note" })}
            />
            <span className="ca-mode-hint">{internal ? t.staffOnly : t.visibleToCustomer}</span>
          </div>
          {replyBlocked && <p className="ca-callout" role="status">{t.resolvedNoReply}</p>}
          {draft.files.length > 0 && (
            <ul className="ca-files">
              {draft.files.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  <span className="ca-file-name">{file.name}</span>
                  <span className="ca-file-size">{formatFileSize(file.size)}</span>
                  <Button variant="neutral" appearance="ghost" size="xs" aria-label={`${t.removeFile}: ${file.name}`} icon={<X size={14} />} onClick={() => inbox.setDraft(selectedId, { files: draft.files.filter((_, i) => i !== index) })} />
                </li>
              ))}
            </ul>
          )}
          {fileError && <p className="ca-error" role="alert">{fileError}</p>}
          <div className="ca-composer-row">
            <input ref={fileInput} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onFiles} />
            <Button variant="neutral" appearance="ghost" onClick={() => fileInput.current?.click()} aria-label={t.attach} title={t.attach} icon={<Paperclip size={18} />} />
            <Button variant="neutral" appearance="ghost" onClick={() => setShowReplies(true)} aria-label={t.savedReplies.button} title={t.savedReplies.button} aria-haspopup="dialog" disabled={replyBlocked} icon={<MessageSquareText size={18} />} />
            <Input
              textarea
              ref={box}
              className="ca-composer-input"
              rows={2}
              aria-label={t.composerLabel}
              placeholder={internal ? t.composerNote : t.composerReply}
              value={draft.text}
              disabled={replyBlocked}
              onChange={(event) => inbox.setDraft(selectedId, { text: event.target.value })}
              onKeyDown={onKeyDown}
              error={tooLong}
            />
            <Button type="submit" variant="primary" appearance="filled" rounded className="ca-send" disabled={!canSend} aria-label={internal ? t.sendNote : t.send} title={internal ? t.sendNote : t.send} icon={<SendHorizontal size={18} />} />
          </div>
        </form>
      )}
      {showReplies && (
        <SavedRepliesDialog
          t={t}
          lang={lang}
          categories={categories}
          conversation={conversation}
          agentName={agentName}
          canManage={access.manageReplies}
          onClose={() => { setShowReplies(false); box.current?.focus(); }}
          onInsert={(text) => inbox.setDraft(selectedId, { text: draft.text.trim() ? `${draft.text.replace(/\s+$/, "")}\n\n${text}` : text })}
        />
      )}
    </div>
  );
}
